import React, { useState, useEffect } from "react";
import styled, { keyframes, css } from "styled-components";
import { Link } from "react-router-dom"; // Import Link
import LazyLoad from "react-lazyload";
import backgroundImage1 from "../assets/images/hero/donate.webp";
import backgroundImage2 from "../assets/images/hero/volunteer.webp";
import backgroundImage3 from "../assets/images/hero/program.webp";
import backgroundImage4 from "../assets/images/hero/restore.webp";

const Hero = () => {
  const slides = [
    {
      image: backgroundImage3,
      title: "JOIN OUR PROGRAMS",
      subtitle: "Start your journey to homeownership with us!",
      buttons: [
        { text: "SIGN UP", href: "/how-to-apply", color: "var(--hhb-primary)" },
      ],
      position: "center",
    },
    {
      image: backgroundImage2,
      title: "MAKE A DIFFERENCE",
      subtitle: "Volunteer with us and bring hope to those in need.",
      buttons: [
        {
          text: "VOLUNTEER",
          href: "/get-involved",
          color: "var(--hhb-orange)",
        },
      ],
      position: "center",
    },
    {
      image: backgroundImage4,
      title: "SHOP AT RESTORE",
      subtitle: "Find great deals while supporting our mission.",
      buttons: [
        { text: "SHOP NOW", href: "/restore", color: "var(--hhb-darkblue)" },
      ],
      position: "center",
    },
    {
      image: backgroundImage1,
      title: "SUPPORT OUR MISSION",
      subtitle: "Your generous donations help us build homes and communities.",
      buttons: [
        { text: "DONATE NOW", href: "/donate", color: "var(--hhb-green)" },
      ],
      position: "center",
    },
  ];

  const [currentSlide, setCurrentSlide] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);

  const preloadImage = (src, callback) => {
    const img = new Image();
    img.src = src;
    img.onload = callback;
  };

  useEffect(() => {
    const interval = setInterval(() => {
      setIsTransitioning(true);

      preloadImage(slides[(currentSlide + 1) % slides.length].image, () => {
        setIsTransitioning(false);
        setCurrentSlide((prev) => (prev + 1) % slides.length);
      });
    }, 10000);

    return () => clearInterval(interval);
  }, [currentSlide, slides]);

  return (
    <LazyLoad height={600} offset={100}>
      <HeroContainer>
        <Slide
          backgroundImage={slides[currentSlide].image}
          isTransitioning={isTransitioning}
        >
          <Overlay />
          <HeroContent>
            <HeroText isTransitioning={isTransitioning}>
              <h1>{slides[currentSlide].title}</h1>
              <p>{slides[currentSlide].subtitle}</p>
              <ButtonContainer>
                {slides[currentSlide].buttons.map((button, index) => (
                  <Button key={index} to={button.href} color={button.color}>
                    {button.text}
                  </Button>
                ))}
              </ButtonContainer>
            </HeroText>
          </HeroContent>
        </Slide>
      </HeroContainer>
    </LazyLoad>
  );
};

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const fadeOut = keyframes`
  from {
    opacity: 1;
  }
  to {
    opacity: 0;
  }
`;

const HeroContainer = styled.div`
  position: relative;
  height: 65vh;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;

  @media (max-width: 768px) {
    height: 60vh;
  }

  @media (min-width: 769px) and (max-width: 1024px) {
    height: 50vh;
  }
`;

const Slide = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  background: url(${(props) => props.backgroundImage}) center/cover no-repeat;
  transition: background-image 1.5s ease-in-out;

  ${({ isTransitioning }) =>
    isTransitioning &&
    css`
      animation: ${fadeOut} 1.5s ease-in-out forwards;
    `}
`;

const Overlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.4);
`;

const HeroContent = styled.div`
  position: relative;
  text-align: center;
  z-index: 1;
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  height: 100%; /* Ensure the content takes up the full height of the hero */

  @media (max-width: 768px) {
    justify-content: flex-end;
    padding-bottom: 10%; /* Slight adjustment for mobile */
  }

  @media (min-width: 769px) and (max-width: 1024px) {
    justify-content: center;
  }
`;

const HeroText = styled.div`
  h1 {
    font-size: 5rem;
    margin: 0;
    text-transform: uppercase;

    @media (max-width: 768px) {
      font-size: 2.5rem;
    }

    @media (min-width: 769px) and (max-width: 1024px) {
      font-size: 3.5rem;
    }
  }

  p {
    font-size: 2rem;

    @media (max-width: 768px) {
      font-size: 1.5rem;
    }

    @media (min-width: 769px) and (max-width: 1024px) {
      font-size: 2rem;
    }
  }

  ${({ isTransitioning }) =>
    isTransitioning
      ? css`
          animation: ${fadeOut} 1s ease-out forwards;
        `
      : css`
          animation: ${fadeInUp} 1s ease-out forwards;
        `}
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  gap: 20px;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 10px;
  }

  @media (min-width: 769px) and (max-width: 1024px) {
    gap: 15px;
  }
`;

const Button = styled(Link)`
  // Use Link instead of a
  display: inline-block;
  padding: 15px 30px;
  font-size: 1.2rem;
  font-weight: bold;
  text-transform: uppercase;
  text-align: center;
  border-radius: 5px;
  text-decoration: none;
  background-color: ${(props) => props.color};
  color: #fff;
  cursor: pointer;
  transition: background-color 0.3s ease, transform 0.3s ease;

  &:hover {
    transform: scale(1.05);
  }

  @media (max-width: 768px) {
    font-size: 1rem;
    padding: 10px 20px;
  }

  @media (min-width: 769px) and (max-width: 1024px) {
    font-size: 1.1rem;
    padding: 12px 25px;
  }
`;

export default Hero;
